/**
 * Shared utility function to scroll to the first form validation error.
 * Scrolls to the section title containing the error (not the error itself) for better context.
 * 
 * @param options - Configuration options
 * @param options.delay - Delay in milliseconds before starting to scroll (default: 300)
 */
export function scrollToFirstError(options: { delay?: number } = {}) {
  const delay = options.delay || 300;

  setTimeout(() => {
    const allErrors = document.querySelectorAll(
      '.lj-alert--error, .ld-alert.ld-error-alert, .lj-input--error, .ld-error-alert, .ld-form-alert.ld-error-alert'
    );

    const firstError = Array.from(allErrors).find((error) => {
      const el = error as HTMLElement;
      return el.id !== 'ajax-error-alert' && !el.closest('.ld-alerts');
    }) as HTMLElement | undefined;

    if (!firstError) return;

    let sectionTitle: HTMLElement | null = firstError.closest('.js-field-section-title') as HTMLElement;
    
    if (!sectionTitle) {
      const parentContainer = firstError.closest('.ld-form-group, .ld-profile-section, section') as HTMLElement;
      
      if (parentContainer) {
        sectionTitle = parentContainer.querySelector('.js-field-section-title') as HTMLElement;
        
        if (!sectionTitle && parentContainer.tagName === 'SECTION') {
          const sectionParent = parentContainer.closest('section') || parentContainer.parentElement?.closest('section');
          sectionTitle = sectionParent?.querySelector('.js-field-section-title') as HTMLElement || null;
        }
        
        if (!sectionTitle) {
          let currentElement: HTMLElement | null = firstError.previousElementSibling as HTMLElement;
          let attempts = 0;
          while (currentElement && attempts < 10) {
            if (currentElement.classList.contains('js-field-section-title') || 
                currentElement.classList.contains('trix-title') ||
                currentElement.tagName === 'LABEL') {
              sectionTitle = currentElement;
              break;
            }
            currentElement = currentElement.previousElementSibling as HTMLElement;
            attempts++;
          }
          
          if (!sectionTitle) {
            const searchContainer = parentContainer.closest('section') || parentContainer;
            const allLabels = searchContainer.querySelectorAll('label.js-field-section-title, .trix-title.js-field-section-title, .ld-form-label.js-field-section-title');
            
            if (allLabels.length > 0) {
              const errorRect = firstError.getBoundingClientRect();
              let closestLabel: HTMLElement | null = null;
              let closestDistance = Infinity;
              
              for (const label of Array.from(allLabels)) {
                const labelRect = label.getBoundingClientRect();
                if (labelRect.top < errorRect.top) {
                  const distance = errorRect.top - labelRect.top;
                  if (distance < closestDistance) {
                    closestDistance = distance;
                    closestLabel = label as HTMLElement;
                  }
                }
              }
              
              sectionTitle = closestLabel || (allLabels[0] as HTMLElement);
            }
          }
        }
      }
    }

    const scrollTarget = sectionTitle || firstError;

    const performScroll = () => {
      const navbar = 
        (document.querySelector('.lj-navBar') as HTMLElement) ||
        (document.querySelector('.ld-wizard-navbar') as HTMLElement) ||
        (document.querySelector('.ld-navbar-top') as HTMLElement) ||
        (document.querySelector('nav[role="navigation"]') as HTMLElement);
      
      const errorBanner = document.querySelector('.ld-top-alert.ld-error-alert') as HTMLElement;
      
      let navbarHeight = 0;
      if (navbar) {
        const navbarRect = navbar.getBoundingClientRect();
        navbarHeight = navbarRect.height || navbar.offsetHeight || navbar.clientHeight || 0;
      }
      if (!navbarHeight || navbarHeight < 60) {
        navbarHeight = 120;
      }
      
      let errorBannerHeight = 0;
      if (errorBanner) {
        const errorBannerRect = errorBanner.getBoundingClientRect();
        errorBannerHeight = errorBannerRect.height || errorBanner.offsetHeight || errorBanner.clientHeight || 0;
      }
      
      const padding = 100;
      const totalOffset = navbarHeight + errorBannerHeight + padding;

      const elementRect = scrollTarget.getBoundingClientRect();
      const currentScrollY = window.pageYOffset || window.scrollY || document.documentElement.scrollTop || 0;
      const elementAbsoluteTop = elementRect.top + currentScrollY;
      const targetScrollY = elementAbsoluteTop - totalOffset;

      window.scrollTo({ 
        top: Math.max(0, targetScrollY), 
        behavior: 'smooth' 
      });
    };

    setTimeout(() => {
      requestAnimationFrame(performScroll);
    }, 100);
  }, delay);
}

// Make it available globally for use in JavaScript files (e.g., error.js.erb)
if (typeof window !== 'undefined') {
  (window as any).scrollToFirstError = scrollToFirstError;
}

